import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ToastService, ToastTone } from './toast.service';
import { IconComponent, IconName } from '../icon.component';

const TONE: Record<ToastTone, string> = {
  success: 'outline-success/40 text-success-fg',
  error: 'outline-danger/40 text-danger-fg',
  info: 'outline-info/40 text-info-fg',
};

/** The icon repeats what the colour says, so the message does not depend on colour. */
const ICON: Record<ToastTone, IconName> = {
  success: 'check',
  error: 'x',
  info: 'info',
};

/** z-[9999] rather than a scale value: the Poseidon layout masks sit at 998/999 and
 *  would swallow the click. Drops to z-50 when that layout goes.
 *
 *  Rendered once in the shell. Bottom-centre on mobile, top-right from sm up.
 *
 *  A toast may carry one action, which is how "Fiets geannuleerd" offers to put the bike
 *  back. That matters on a full class, where cancelling can mean not getting the spot
 *  again, so the reversal has to be one tap and not a re-navigation.
 *
 *  The enter animation is the `starting:` variant, so it costs no JS and no animation
 *  library, and it is skipped for anyone who asked for reduced motion. */
@Component({
  selector: 'app-toast-outlet',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  template: `
    <div
      aria-live="assertive"
      class="pointer-events-none fixed inset-0 z-[9999] flex items-end px-4 py-6 sm:items-start sm:p-6"
    >
      <div class="flex w-full flex-col items-center gap-2 sm:items-end">
        @for (toast of toasts(); track toast.id) {
          <div
            class="pointer-events-auto w-full max-w-sm rounded-md bg-surface-card px-4 py-3 text-sm shadow-lg outline transition duration-300 starting:translate-y-2 starting:opacity-0 motion-reduce:transition-none"
            [class]="tone[toast.tone]"
          >
            <div class="flex items-start gap-3">
              <app-icon
                [name]="icon[toast.tone]"
                size="size-5"
                class="mt-0.5 shrink-0"
              />
              <div class="flex-1">
                <p class="text-fg">{{ toast.message }}</p>
                @if (toast.action; as action) {
                  <button
                    type="button"
                    class="mt-2 -mb-1 min-h-8 text-sm font-semibold text-brand hover:underline"
                    (click)="run(toast.id, action)"
                  >
                    {{ action.label }}
                  </button>
                }
              </div>
              <button
                type="button"
                class="-m-1 shrink-0 p-1 text-fg-subtle hover:text-fg"
                (click)="dismiss(toast.id)"
              >
                <app-icon name="x" size="size-4" />
                <span class="sr-only">Sluiten</span>
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  `,
})
export class ToastOutletComponent {
  private readonly service = inject(ToastService);
  protected readonly toasts = this.service.toasts;
  protected readonly tone = TONE;
  protected readonly icon = ICON;

  protected dismiss(id: number) {
    this.service.dismiss(id);
  }

  /** Close first: the action usually raises its own toast, and two stacked messages
   *  about the same bike read as a glitch. */
  protected run(id: number, action: { run: () => void | Promise<void> }) {
    this.service.dismiss(id);
    void action.run();
  }
}
